import { calculateMonthlyPayment, calculateMortgageExtras } from '../calculations'

interface HomeAffordabilityInput {
  grossMonthlyIncome: number
  monthlyDebts: number // car payments, student loans, credit cards, etc
  downPayment: number
  interestRate: number // annual %
  loanTerm: number // months (typically 360)
  propertyTaxRate: number // annual %
  annualInsurance: number
  monthlyHOA: number
  pmiRate: number // annual %
}

export function calculateHomeAffordability(input: HomeAffordabilityInput) {
  // DTI rules
  const maxFrontEnd = input.grossMonthlyIncome * 0.28 // housing only
  const maxBackEnd = input.grossMonthlyIncome * 0.36 // housing + all debts
  const maxHousingFromBackEnd = maxBackEnd - input.monthlyDebts

  // Use the more restrictive of front-end and back-end
  const maxTotalHousingPayment = Math.min(maxFrontEnd, maxHousingFromBackEnd)

  // Back-solve: max P&I from max total housing payment
  // total housing = P&I + property tax + insurance + PMI + HOA
  // We need to iterate because PMI depends on loan amount which depends on home price
  let maxHomePrice = 0
  let maxLoanAmount = 0
  let maxPI = 0

  // Iterative solver
  for (let price = 50000; price <= 2000000; price += 1000) {
    const loan = price - input.downPayment
    if (loan <= 0) continue

    const extras = calculateMortgageExtras(price, loan, {
      propertyTaxRate: input.propertyTaxRate,
      pmiRate: input.downPayment / price >= 0.20 ? 0 : input.pmiRate,
      annualInsurance: input.annualInsurance,
      monthlyHOA: input.monthlyHOA,
    })

    const pi = calculateMonthlyPayment(loan, input.interestRate, input.loanTerm)
    const totalPayment = pi + extras.totalMonthlyExtras

    if (totalPayment <= maxTotalHousingPayment) {
      maxHomePrice = price
      maxLoanAmount = loan
      maxPI = pi
    } else {
      break
    }
  }

  // Calculate details for max affordable price
  const needsPMI = input.downPayment < maxHomePrice * 0.20
  const extras = maxHomePrice > 0 ? calculateMortgageExtras(maxHomePrice, maxLoanAmount, {
    propertyTaxRate: input.propertyTaxRate,
    pmiRate: needsPMI ? input.pmiRate : 0,
    annualInsurance: input.annualInsurance,
    monthlyHOA: input.monthlyHOA,
  }) : { monthlyPropertyTax: 0, monthlyInsurance: 0, monthlyPMI: 0, monthlyHOA: 0, totalMonthlyExtras: 0 }

  const totalMonthlyPayment = maxPI + extras.totalMonthlyExtras
  const frontEndDTI = input.grossMonthlyIncome > 0 ? (totalMonthlyPayment / input.grossMonthlyIncome) * 100 : 0
  const backEndDTI = input.grossMonthlyIncome > 0 ? ((totalMonthlyPayment + input.monthlyDebts) / input.grossMonthlyIncome) * 100 : 0

  const downPaymentPercent = maxHomePrice > 0 ? (input.downPayment / maxHomePrice) * 100 : 0
  const totalInterest = (maxPI * input.loanTerm) - maxLoanAmount

  return {
    maxHomePrice,
    maxLoanAmount: Math.round(maxLoanAmount),
    downPayment: input.downPayment,
    downPaymentPercent: Math.round(downPaymentPercent * 10) / 10,
    monthlyPI: Math.round(maxPI),
    monthlyPropertyTax: extras.monthlyPropertyTax,
    monthlyInsurance: extras.monthlyInsurance,
    monthlyPMI: extras.monthlyPMI,
    monthlyHOA: extras.monthlyHOA,
    totalMonthlyPayment: Math.round(totalMonthlyPayment),
    frontEndDTI: Math.round(frontEndDTI * 10) / 10,
    backEndDTI: Math.round(backEndDTI * 10) / 10,
    maxFrontEnd: Math.round(maxFrontEnd),
    maxBackEnd: Math.round(maxBackEnd),
    maxHousingFromBackEnd: Math.round(maxHousingFromBackEnd),
    needsPMI,
    totalInterest: Math.round(Math.max(0, totalInterest)),
    monthlyDebts: input.monthlyDebts,
  }
}
